import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Tooltip
 *
 * Portal-based tooltip that works correctly inside scrollable containers,
 * overflow:hidden parents, and near viewport edges.
 */

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

export interface TooltipProps {
	content: React.ReactNode
	title?: string
	side?: TooltipSide
	delayMs?: number
	disabled?: boolean
	children: React.ReactElement
}

const OFFSET = 8

function getTooltipPosition(triggerRect: DOMRect, tooltipRect: DOMRect, side: TooltipSide) {
	const scrollX = window.scrollX
	const scrollY = window.scrollY

	switch (side) {
		case 'top':
			return {
				top: triggerRect.top + scrollY - tooltipRect.height - OFFSET,
				left: triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2,
			}
		case 'bottom':
			return {
				top: triggerRect.bottom + scrollY + OFFSET,
				left: triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2,
			}
		case 'left':
			return {
				top: triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2,
				left: triggerRect.left + scrollX - tooltipRect.width - OFFSET,
			}
		case 'right':
			return {
				top: triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2,
				left: triggerRect.right + scrollX + OFFSET,
			}
	}
}

const arrowStyles: Record<TooltipSide, string> = {
	top: 'top-full left-1/2 -translate-x-1/2 border-t-gold border-x-transparent border-b-transparent border-4',
	bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gold border-x-transparent border-t-transparent border-4',
	left: 'left-full top-1/2 -translate-y-1/2 border-l-gold border-y-transparent border-r-transparent border-4',
	right: 'right-full top-1/2 -translate-y-1/2 border-r-gold border-y-transparent border-l-transparent border-4',
}

const enterVariants: Record<TooltipSide, { initial: Record<string, number>; animate: Record<string, number> }> = {
	top: { initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 } },
	bottom: { initial: { opacity: 0, y: -4 }, animate: { opacity: 1, y: 0 } },
	left: { initial: { opacity: 0, x: 4 }, animate: { opacity: 1, x: 0 } },
	right: { initial: { opacity: 0, x: -4 }, animate: { opacity: 1, x: 0 } },
}

function TooltipPortal({
	triggerRef,
	side,
	title,
	content,
	tooltipId,
}: {
	triggerRef: React.RefObject<HTMLElement | null>
	side: TooltipSide
	title?: string
	content: React.ReactNode
	tooltipId: string
}) {
	const tooltipRef = React.useRef<HTMLDivElement>(null)
	const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null)

	React.useLayoutEffect(() => {
		const trigger = triggerRef.current
		const tooltip = tooltipRef.current
		if (!trigger || !tooltip) return
		setPos(getTooltipPosition(trigger.getBoundingClientRect(), tooltip.getBoundingClientRect(), side))
	}, [side, triggerRef])

	const enter = enterVariants[side]

	return ReactDOM.createPortal(
		<motion.div
			ref={tooltipRef}
			id={tooltipId}
			role="tooltip"
			className="z-tooltip pointer-events-none"
			style={pos ? { position: 'absolute', top: pos.top, left: pos.left } : { visibility: 'hidden', position: 'absolute', top: 0, left: 0 }}
			initial={enter.initial}
			animate={pos ? enter.animate : enter.initial}
			exit={{ opacity: 0 }}
			transition={{ type: 'spring', stiffness: 500, damping: 30, mass: 0.5 }}
		>
			<div className={cn('relative bg-navy-900 text-cream-50 rounded-md px-3 py-2 shadow-lg max-w-xs font-ui text-xs leading-relaxed')}>
				{title && <div className="font-semibold text-gold mb-0.5">{title}</div>}
				{content}
				<div className={cn('absolute w-0 h-0', arrowStyles[side])} aria-hidden="true" />
			</div>
		</motion.div>,
		document.body,
	)
}

export function Tooltip({ content, title, side = 'top', delayMs = 300, disabled = false, children }: TooltipProps) {
	const [visible, setVisible] = React.useState(false)
	const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
	const triggerRef = React.useRef<HTMLElement>(null)
	const tooltipId = React.useId()

	const show = React.useCallback(() => {
		if (disabled) return
		timeoutRef.current = setTimeout(() => setVisible(true), delayMs)
	}, [disabled, delayMs])

	const hide = React.useCallback(() => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current)
		setVisible(false)
	}, [])

	React.useEffect(() => () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current)
	}, [])

	const child = children as React.ReactElement<Record<string, unknown>>
	const onMouseEnter = child.props.onMouseEnter as ((e: React.MouseEvent) => void) | undefined
	const onMouseLeave = child.props.onMouseLeave as ((e: React.MouseEvent) => void) | undefined
	const onFocus = child.props.onFocus as ((e: React.FocusEvent) => void) | undefined
	const onBlur = child.props.onBlur as ((e: React.FocusEvent) => void) | undefined

	const trigger = React.cloneElement(child, {
		ref: triggerRef,
		onMouseEnter: (e: React.MouseEvent) => {
			show()
			onMouseEnter?.(e)
		},
		onMouseLeave: (e: React.MouseEvent) => {
			hide()
			onMouseLeave?.(e)
		},
		onFocus: (e: React.FocusEvent) => {
			show()
			onFocus?.(e)
		},
		onBlur: (e: React.FocusEvent) => {
			hide()
			onBlur?.(e)
		},
		'aria-describedby': visible ? tooltipId : undefined,
	})

	return (
		<>
			{trigger}
			<AnimatePresence>
				{visible && <TooltipPortal key="tooltip" triggerRef={triggerRef} side={side} title={title} content={content} tooltipId={tooltipId} />}
			</AnimatePresence>
		</>
	)
}
